import type { WorkflowContract } from '../generated/api';

interface StepContract {
  outputContract?: WorkflowContract;
  negotiationStatus?: string;
  version?: number;
}

/**
 * Compute a deterministic hash of a step's output contract.
 * Must produce the same result as server-side computeContractHash.
 *
 * Returns a 16-char hex string or null if no contract.
 */
export async function computeContractHash(contract: StepContract | undefined | null): Promise<string | null> {
  const schema = contract?.outputContract?.jsonSchema ?? contract?.outputContract?.schema;
  if (!schema) return null;

  const json = JSON.stringify(sortJson(schema));
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(json));
  const hex = Array.from(new Uint8Array(buf))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
  return hex.slice(0, 16);
}

function sortJson(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(sortJson);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, child]) => [key, sortJson(child)]),
  );
}
